/**
 * Knowledge promotion: the items of the selected brand, the creation runs (followed while one runs),
 * the articles to generate from, and every action on an item. Each action refreshes the items it
 * changes, and the older knowledge list that reads the same documents.
 */

import { useAtom, useAtomValue } from 'jotai';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { invalidateQueries } from '@/lib/query-client';
import { knowledgePromotionService } from '@/services/knowledge-promotion.service';
import { selectedBusinessUnitIdAtom } from '@/stores/business-unit.store';
import type { KnowledgeEdit } from '@/modules/knowledge-promotion/types';

const JOB_POLL_MS = 3000;
export const knowledgeKey = (...parts: string[]): string[] => ['knowledge-promotion', ...parts];

const refreshItems = (): void => {
	invalidateQueries(knowledgeKey('items'));
	invalidateQueries(['promoted-blueprints', 'knowledge']);
};

const jobsQueryAtom = atomWithQuery(() => ({
	queryKey: knowledgeKey('jobs'),
	queryFn: () => knowledgePromotionService.jobs(),
	staleTime: 0,
	refetchInterval: (query) => (query.state.data?.some((job) => job.status === 'running') ? JOB_POLL_MS : false)
}));

const itemsQueryAtom = atomWithQuery((get) => {
	// A running job adds items as it goes: follow it.
	const running = get(jobsQueryAtom).data?.some((job) => job.status === 'running') ?? false;
	return {
		queryKey: knowledgeKey('items', buScopeKey(get)),
		queryFn: () => knowledgePromotionService.list(buScopeParams(get)),
		staleTime: 0,
		refetchInterval: running ? JOB_POLL_MS : false
	};
});

const articlesQueryAtom = atomWithQuery((get) => ({
	queryKey: knowledgeKey('articles', buScopeKey(get)),
	queryFn: () => knowledgePromotionService.articles(buScopeParams(get)),
	staleTime: 60 * 1000
}));

const itemMutation = <V extends { id: string }, R>(name: string, run: (variables: V) => Promise<R>) =>
	atomWithMutation(() => ({
		mutationKey: knowledgeKey(name),
		mutationFn: run,
		onSettled: refreshItems
	}));

const editAtom = itemMutation('edit', ({ id, edit }: { id: string; edit: KnowledgeEdit }) => knowledgePromotionService.edit(id, edit));
const checkAtom = itemMutation('check', ({ id }: { id: string }) => knowledgePromotionService.check(id));
const promoteAtom = itemMutation('promote', ({ id }: { id: string }) => knowledgePromotionService.promote(id));
const stopAtom = itemMutation('stop', ({ id }: { id: string }) => knowledgePromotionService.stop(id));
const removeAtom = itemMutation('remove', ({ id }: { id: string }) => knowledgePromotionService.remove(id));
const discardEditAtom = itemMutation('discard-edit', ({ id }: { id: string }) => knowledgePromotionService.discardPending(id));
const autoSendAtom = itemMutation('auto-send', ({ id, autoSend }: { id: string; autoSend: boolean }) =>
	knowledgePromotionService.setAutoSend(id, autoSend)
);

const startedJob = () => {
	invalidateQueries(knowledgeKey('jobs'));
	refreshItems();
};

const generateAtom = atomWithMutation(() => ({
	mutationKey: knowledgeKey('generate'),
	mutationFn: ({ articleId, buId, replace }: { articleId: string; buId: string | null; replace?: boolean }) =>
		knowledgePromotionService.generate(articleId, buId ? { bu_id: buId } : {}, replace === true),
	onSuccess: startedJob
}));

const importAtom = atomWithMutation(() => ({
	mutationKey: knowledgeKey('import'),
	mutationFn: ({ text, buId }: { text: string; buId: string | null }) =>
		knowledgePromotionService.importPairs(text, buId ? { bu_id: buId } : {}),
	onSuccess: startedJob
}));

const previewAtom = atomWithMutation(() => ({
	mutationKey: knowledgeKey('import-preview'),
	mutationFn: ({ text }: { text: string }) => knowledgePromotionService.previewImport(text)
}));

const tryAtom = atomWithMutation(() => ({
	mutationKey: knowledgeKey('try'),
	mutationFn: ({ question, itemId, buId }: { question: string; itemId: string | null; buId: string | null }) =>
		knowledgePromotionService.tryQuestion(question, itemId, buId ? { bu_id: buId } : {})
}));

export const useKnowledgeItems = () => useAtom(itemsQueryAtom)[0];
export const useKnowledgeJobs = () => useAtom(jobsQueryAtom)[0];
export const useKnowledgeArticles = () => useAtom(articlesQueryAtom)[0];
export const useSelectedBuId = (): string | null => useAtomValue(selectedBusinessUnitIdAtom) ?? null;

export const useEditKnowledge = () => useAtom(editAtom)[0];
export const useCheckKnowledge = () => useAtom(checkAtom)[0];
export const usePromoteKnowledge = () => useAtom(promoteAtom)[0];
export const useStopKnowledge = () => useAtom(stopAtom)[0];
export const useRemoveKnowledge = () => useAtom(removeAtom)[0];
export const useDiscardKnowledgeEdit = () => useAtom(discardEditAtom)[0];
export const useKnowledgeAutoSend = () => useAtom(autoSendAtom)[0];
export const useGenerateKnowledge = () => useAtom(generateAtom)[0];
export const useImportKnowledge = () => useAtom(importAtom)[0];
export const usePreviewImport = () => useAtom(previewAtom)[0];
export const useTryKnowledge = () => useAtom(tryAtom)[0];
